import React, { useEffect, useRef, useState } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import AboutStory from '../components/AboutStory';
import Features from '../components/Features';
import Stats from '../components/Stats';
import Testimonials from '../components/Testimonials';
import CTA from '../components/CTA';
import Footer from '../components/Footer';
import { initScrollReveal } from '../utils/initScrollReveal';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const rootRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    gsap.config({
      autoSleep: 60,
      force3D: true,
      nullTargetWarn: false,
    });

    let cleanupReveal = () => { };

    // Delay reveal init by one rAF so all child components have fully painted
    // before we lock in ScrollTrigger positions.
    const rafId = requestAnimationFrame(() => {
      cleanupReveal = initScrollReveal(rootRef.current || document.body);
    });

    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      cleanupReveal();
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When opening `/` with a hash (e.g. from Privacy page nav), scroll to that section after paint.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return undefined;
    const NAV_OFFSET = 88;
    const id = window.setTimeout(() => {
      const section = document.getElementById(hash);
      if (section) {
        const y = section.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }, 120);
    return () => window.clearTimeout(id);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <ParallaxProvider>
      <div ref={rootRef} className="overflow-x-hidden min-h-screen bg-gray-900">
        <Navbar />
        <Hero />
        <AboutStory />
        <Testimonials />
        <Features />
        {/* <Services /> */}
        <Stats />
        <CTA />
        <Footer />

        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          className={`fixed right-4 bottom-24 z-50 sm:right-6 sm:bottom-24 md:bottom-28 lg:bottom-24 group transition-all duration-300 ${showBackToTop
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
            }`}
        >
          <span className="relative flex justify-center items-center w-12 h-12 rounded-2xl border backdrop-blur-xl shadow-lg bg-gray-900/70 border-[#F47D11]/40 shadow-[#F47D11]/20 transition-all duration-300 group-hover:scale-105 group-hover:border-[#F47D11]/70 group-hover:shadow-[#F47D11]/40 group-active:scale-95">
            <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#F47D11]/15 to-[#F4733A]/15" />
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="relative w-5 h-5 text-[#F47D11]"
              aria-hidden="true"
            >
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </span>
        </button>
      </div>
    </ParallaxProvider>
  );
};

export default Home;

